/**
 * Enumerate blog slugs available on disk for a given language.
 *
 * Read at build time by vite-react-ssg's `getStaticPaths` to determine
 * which `/:lang/blog/:slug` URLs to prerender. We only emit a path when
 * the per-language translation file actually exists — listing a URL with
 * no content would produce a soft-404 from Google.
 *
 * Runs in Node during SSG; the import.meta.glob call is statically
 * analysed by Vite so it works in both ESM and SSR contexts.
 */

import type { BlogLang } from '@/lib/blogPosts';

// `eager: true` + `query: '?url'` returns string URLs we can parse for slugs
// without parsing JSON. The keys are absolute project paths like
// '/src/content/blog/hr/foo.json'.
const allBlogFiles = import.meta.glob('/src/content/blog/*/*.json', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

export function collectBlogSlugs(lang: BlogLang): string[] {
  const prefix = `/src/content/blog/${lang}/`;
  const slugs: string[] = [];

  for (const filePath of Object.keys(allBlogFiles)) {
    if (!filePath.startsWith(prefix)) continue;
    const filename = filePath.slice(prefix.length); // e.g. "fiskalizacija-2-0-...json"
    if (!filename.endsWith('.json')) continue;
    slugs.push(filename.replace(/\.json$/, ''));
  }

  return slugs;
}
